<script setup lang="ts">
// /examples/booking — every supported way to put the Workspace369 scheduler on
// a website, running live against the Apex Auto Spa demo workspace.
//
// Everything shown here comes from utils/examples/bookingExamples.ts; this file
// only wires each recipe up so the thing beside the code is the code running.
//
// The page loads ONE booking.js tag (popup mode) through useHead — which puts
// it in <head>, where the inline script-tag placement cannot render. That is
// exactly why the inline example is mounted with W369Booking.mount() instead,
// and the code shown for it is still the script-tag form a normal page pastes
// into its body.
import ExampleSection from '~/components/examples/ExampleSection.vue'
import CodeBlock from '~/components/examples/CodeBlock.vue'
import {
  BOOKING_DOCS_LINKS,
  BOOKING_EXAMPLE_LAYOUT_PAGES,
  BOOKING_EXAMPLE_THEMES,
  DEMO_WORKSPACE_ID,
  IFRAME_CREDIT,
  bookingExamplePublicUrl,
  bookingExamples,
} from '~/utils/examples/bookingExamples'

interface W369MountHandle { destroy: () => void }
interface W369BookingApi {
  open: (options?: Record<string, unknown>) => void
  close: () => void
  mount: (target: Element | string, options?: Record<string, unknown>) => W369MountHandle | null
}

const DESCRIPTION = 'Live, copy-paste examples of every way to embed the Workspace369 scheduler: inline, popup, your own trigger, your own form, your own modal, a plain iframe, and the page language.'

useSeoMeta({
  title: 'Booking embed examples',
  description: DESCRIPTION,
  ogTitle: 'Booking embed examples',
  ogDescription: DESCRIPTION,
  robots: 'index, follow',
})

useHead({
  script: [{
    // Same-origin on purpose. The copy-paste samples all show the absolute
    // https://369.dev/booking.js a visitor's own site needs, but THIS page must
    // run the booking.js it was built with — otherwise the examples silently
    // demonstrate whatever is currently deployed instead of what is documented
    // beside them (mount() would be missing, and nothing would render).
    'src': '/booking.js',
    'data-booking': '',
    'data-workspace': DEMO_WORKSPACE_ID,
    // The floating button this tag renders IS the popup example (§02), so it
    // must open the page §02 documents. Without data-page it would silently
    // open the default `book` page while the copy beside it says round-robin.
    // Every other consumer on this page names its page explicitly too: the
    // inline mount and the form pass `book`, the modal `intake-forms`, the
    // trigger link carries data-page="choose-specialist".
    'data-page': 'round-robin-team',
    'data-mode': 'popup',
    'data-label': 'Book a detail',
    'async': true,
  }],
})

const iframeExampleUrl = `${bookingExamplePublicUrl('deposit-review')}?embed=1`

const form = reactive({
  name: 'Ada Lovelace',
  email: 'ada@example.com',
  phone: '+14155550100',
  company: 'Analytical Engines',
})

let bookingApi: W369BookingApi | null = null
let inlineHandle: W369MountHandle | null = null
// The Language example (§08): the default page again, pinned to Polish.
let languageHandle: W369MountHandle | null = null
let modalHandle: W369MountHandle | null = null
const modalEl = ref<HTMLDialogElement | null>(null)
// Every w369:ready listener this page registers, abortable in one call. A
// normal host page never needs it (booking.js is page-lifetime), but this one
// unmounts on a client-side route change — see the teardown note below.
const readyListeners = new AbortController()

/**
 * The helper from the docs, plus that abort signal. booking.js is loaded async
 * here too, so EVERY entry point on this page goes through it: a visitor who
 * clicks before the script has loaded must still get the scheduler, not a
 * silent no-op, which is exactly what `bookingApi?.open()` was.
 */
function w369Ready(fn: (api: W369BookingApi) => void) {
  const existing = (window as any).W369Booking as W369BookingApi | undefined
  if (existing && typeof existing.mount === 'function') fn(existing)
  else window.addEventListener('w369:ready', (event: any) => fn(event.detail.api), { once: true, signal: readyListeners.signal })
}

function openPopupExample() {
  w369Ready(api => api.open({ page: 'round-robin-team' }))
}

function submitFormExample() {
  w369Ready(api => api.open({
    page: 'book',
    service: 'demo_booking_express_wash',
    step: 'time',
    // The form above already asked for the details, so the frame opens on the
    // minimal layout: a time picker and a confirm step, no page chrome (C6).
    layout: 'minimal',
    prefill: {
      name: form.name,
      email: form.email,
      phone: form.phone,
      metadata: { company: form.company, source: 'examples' },
    },
  }))
}

function openModalExample() {
  // The dialog opens NOW; the mount happens whenever booking.js is ready. A
  // frame mounted into a hidden container re-measures itself when the
  // container becomes visible, so the order of these two never matters.
  modalEl.value?.showModal()
  w369Ready((api) => {
    // The visitor may have closed the dialog again while the script loaded.
    if (!modalEl.value?.open) return
    modalHandle = api.mount('#modal-slot', {
      page: 'intake-forms',
      step: 'time',
      layout: 'minimal',
      // This page's own ink, so the frame's accent matches the dialog around it.
      accent: '#111827',
      prefill: {
        name: form.name,
        email: form.email,
        phone: form.phone,
        intake: {
          vehicle_year_make_model: '2022 Tesla Model 3',
          detail_priorities: ['Odor removal', 'Leather conditioning'],
        },
      },
    })
  })
}

/** destroy() is idempotent, so the button path and the Escape path may both run. */
function destroyModalMount() {
  modalHandle?.destroy()
  modalHandle = null
}

function closeModalExample() {
  destroyModalMount()
  modalEl.value?.close()
}

/** The dialog's `close` event is the Escape path (and any host-driven close). */
function onModalClosed() {
  destroyModalMount()
}

onMounted(() => {
  w369Ready((api) => {
    bookingApi = api
    inlineHandle = api.mount('#inline-slot', { page: 'book' })
    languageHandle = api.mount('#language-slot', { page: 'book', lang: 'pl' })
  })
})

/**
 * booking.js has NO uninstall API, by design: it is a page-lifetime script on
 * every real host page, so nothing there ever unmounts it. This page is the one
 * consumer that does — a client-side route change away from /examples/booking
 * unmounts the component while useHead takes the <script> tag out of <head> —
 * and what the install placed stays behind unless it is removed here by hand.
 * The floating launcher would then follow the visitor onto every other page of
 * this site, with a dead API behind it.
 *
 * So: the three mounts, the overlay, the launcher container, the global, and any
 * w369:ready listener that never fired. The launcher's credit needs no line of
 * its own — it lives inside the launcher container's shadow root and goes with
 * it, and each mount's credit went with its handle above.
 *
 * If booking.js ever grows a real teardown, this whole block becomes one call.
 */
onBeforeUnmount(() => {
  inlineHandle?.destroy()
  languageHandle?.destroy()
  modalHandle?.destroy()
  bookingApi?.close()
  readyListeners.abort()
  document.querySelectorAll('.w369-booking-launcher').forEach(el => el.remove())
  delete (window as any).W369Booking
})
</script>

<template>
  <div data-theme="corporate" class="examples-root">
    <div class="examples-shell">
      <header class="examples-hero">
        <p class="examples-kicker">Workspace369 · Booking</p>
        <h1 class="examples-title">Booking embed examples</h1>
        <p class="examples-lede">
          Pick the setup that matches your site, copy the block, replace the workspace id and page slug
          (Share &amp; embed in the app gives you both).
        </p>
        <p class="examples-meta">
          Everything here books a demo workspace, so anything you book is test data.
        </p>
        <ul class="examples-docs">
          <li v-for="link in BOOKING_DOCS_LINKS" :key="link.href">
            <a :href="link.href" target="_blank" rel="noopener">{{ link.label }}</a>
            <span>{{ link.note }}</span>
          </li>
        </ul>
      </header>

      <nav class="examples-toc" aria-label="Examples">
        <a v-for="(example, i) in bookingExamples" :key="example.id" :href="`#${example.id}`">
          <span class="examples-toc-index">{{ String(i + 1).padStart(2, '0') }}</span>{{ example.title }}
        </a>
      </nav>

      <ExampleSection :example="bookingExamples[0]!" :index="1">
        <template #demo>
          <!-- A real booking page is ~2,600px tall and dominated everything
               under it. Boxed and scrolled here; on a host page the frame grows
               to its own height, which is the whole point of the inline embed. -->
          <div class="inline-stage">
            <div class="inline-stage-scroll">
              <div id="inline-slot" class="inline-slot" />
            </div>
          </div>
          <p class="demo-hint">Scrolls inside this box on the examples page; on your site the frame grows to its full height.</p>
        </template>
      </ExampleSection>

      <ExampleSection :example="bookingExamples[1]!" :index="2">
        <template #demo>
          <button type="button" class="demo-button" @click="openPopupExample">
            Open the popup
          </button>
        </template>
      </ExampleSection>

      <ExampleSection :example="bookingExamples[2]!" :index="3">
        <template #demo>
          <a
            :href="bookingExamplePublicUrl('choose-specialist')"
            data-w369-booking
            data-page="choose-specialist"
            class="demo-link"
          >Choose your specialist</a>
        </template>
      </ExampleSection>

      <ExampleSection :example="bookingExamples[3]!" :index="4">
        <template #demo>
          <form class="demo-form" @submit.prevent="submitFormExample">
            <label>
              <span>Name</span>
              <input v-model="form.name" type="text" autocomplete="name">
            </label>
            <label>
              <span>Email</span>
              <input v-model="form.email" type="email" autocomplete="email">
            </label>
            <label>
              <span>Phone</span>
              <input v-model="form.phone" type="tel" inputmode="tel" placeholder="+14155550100">
              <small>Include the country code, for example +1 415 555 0100</small>
            </label>
            <label>
              <span>Company</span>
              <input v-model="form.company" type="text">
            </label>
            <button type="submit" class="demo-button">Pick a time</button>
          </form>
        </template>
      </ExampleSection>

      <ExampleSection :example="bookingExamples[4]!" :index="5">
        <template #demo>
          <button type="button" class="demo-button" @click="openModalExample">
            Open our own modal
          </button>
        </template>
      </ExampleSection>

      <ExampleSection :example="bookingExamples[5]!" :index="6">
        <template #demo>
          <iframe
            :src="iframeExampleUrl"
            title="Book an appointment"
            width="100%"
            height="900"
            style="border: 0; display: block;"
            loading="lazy"
          />
          <!-- The credit is part of the snippet beside this frame — a bare
               iframe has no script to inject one — so the live demo has to
               carry it too, and from the same string the snippet is built
               from rather than a hand-built lookalike. -->
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="iframe-credit" v-html="IFRAME_CREDIT" />
        </template>
      </ExampleSection>

      <ExampleSection :example="bookingExamples[6]!" :index="7">
        <template #demo>
          <p class="demo-chip-label">Layouts and scheduling rules</p>
          <div class="demo-chips">
            <a
              v-for="page in BOOKING_EXAMPLE_LAYOUT_PAGES"
              :key="page.slug"
              :href="bookingExamplePublicUrl(page.slug)"
              target="_blank"
              rel="noopener"
              class="demo-chip"
            >
              {{ page.label }}<span>{{ page.note }}</span>
            </a>
          </div>
          <p class="demo-chip-label">Themes</p>
          <div class="demo-chips">
            <a
              v-for="theme in BOOKING_EXAMPLE_THEMES"
              :key="theme"
              :href="bookingExamplePublicUrl(`theme-${theme}` as any)"
              target="_blank"
              rel="noopener"
              class="demo-chip"
            >{{ theme }}</a>
          </div>
        </template>
      </ExampleSection>

      <ExampleSection :example="bookingExamples[7]!" :index="8">
        <template #demo>
          <!-- Same box-and-scroll treatment as the inline example (§01): this
               is that page again, with data-lang="pl" and nothing else. -->
          <div class="inline-stage">
            <div class="inline-stage-scroll">
              <div id="language-slot" class="inline-slot" />
            </div>
          </div>
          <p class="demo-hint">Scrolls inside this box on the examples page; on your site the frame grows to its full height.</p>
        </template>
      </ExampleSection>

      <section class="examples-outro">
        <h2>Phone numbers</h2>
        <p>
          Every phone number needs its country code: a <code>+</code>, the country code, then the number.
          <code>+14155550100</code>, <code>+442071234567</code> and <code>+48221234567</code> all work.
          Anything else reaches the scheduler for the visitor to correct.
        </p>
        <CodeBlock
          label="JavaScript"
          code="W369Booking.open({ prefill: { phone: '+14155550100' } });"
        />
      </section>

      <footer class="examples-footer">
        <p>
          Workspace ID and page slug for your own embeds live under Share &amp; embed on any booking page in the app.
        </p>
      </footer>
    </div>

    <dialog ref="modalEl" class="demo-modal" @close="onModalClosed">
      <div class="demo-modal-head">
        <strong>Book a detail</strong>
        <button type="button" aria-label="Close" @click="closeModalExample">Close</button>
      </div>
      <div id="modal-slot" class="demo-modal-slot" />
    </dialog>
  </div>
</template>

<style scoped>
.examples-root {
  min-height: 100vh;
  background: var(--color-base-100);
  color: var(--color-base-content);
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}

.examples-shell {
  max-width: 880px;
  margin: 0 auto;
  padding: 72px 24px 96px;
  text-align: left;
}

.examples-kicker {
  margin: 0 0 14px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: color-mix(in oklab, var(--color-base-content) 45%, transparent);
}

.examples-title {
  margin: 0 0 18px;
  font-size: 38px;
  line-height: 1.15;
  font-weight: 650;
  letter-spacing: -0.025em;
}

.examples-lede {
  margin: 0 0 14px;
  max-width: 64ch;
  font-size: 17px;
  line-height: 1.65;
  color: color-mix(in oklab, var(--color-base-content) 74%, transparent);
}

.examples-meta {
  margin: 0 0 26px;
  max-width: 64ch;
  font-size: 14px;
  line-height: 1.6;
  color: color-mix(in oklab, var(--color-base-content) 54%, transparent);
}

.examples-docs {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.examples-docs li {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  font-size: 13.5px;
}

.examples-docs a {
  color: var(--color-base-content);
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid color-mix(in oklab, var(--color-base-content) 30%, transparent);
}

.examples-docs a:hover {
  border-bottom-color: var(--color-base-content);
}

.examples-docs span {
  color: color-mix(in oklab, var(--color-base-content) 50%, transparent);
}

.examples-toc {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;
  margin-top: 40px;
}

.examples-toc a {
  display: inline-flex;
  align-items: baseline;
  gap: 7px;
  padding: 6px 12px;
  border: 1px solid var(--color-base-300);
  border-radius: 999px;
  font-size: 13px;
  color: color-mix(in oklab, var(--color-base-content) 72%, transparent);
  text-decoration: none;
  transition: color 120ms ease, border-color 120ms ease;
}

.examples-toc a:hover {
  color: var(--color-base-content);
  border-color: color-mix(in oklab, var(--color-base-content) 32%, transparent);
}

.examples-toc-index {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: color-mix(in oklab, var(--color-base-content) 40%, transparent);
}

/*
 * The inline demo is a full booking page — around 2,600px on a wide screen —
 * so it scrolls inside a fixed box here rather than burying the six examples
 * under it. The fade tells the visitor the frame continues past the fold.
 */
.inline-stage {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background: var(--color-base-100);
}

.inline-stage-scroll {
  height: 640px;
  overflow-y: auto;
}

.inline-stage::after {
  content: "";
  position: absolute;
  inset: auto 0 0;
  height: 56px;
  pointer-events: none;
  background: linear-gradient(to bottom, transparent, var(--color-base-100));
}

.inline-slot {
  min-height: 120px;
}

/* The anchor inside carries the snippet's own inline styles — this wrapper
   only gives it a line box of its own under the frame. */
.iframe-credit {
  line-height: 1;
}

.demo-button {
  display: inline-flex;
  align-items: center;
  padding: 9px 16px;
  border: 1px solid var(--color-base-content);
  border-radius: 8px;
  background: var(--color-base-content);
  color: var(--color-base-100);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 120ms ease;
}

.demo-button:hover {
  opacity: 0.86;
}

.demo-link {
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-base-content);
  text-decoration: none;
  border-bottom: 1px solid color-mix(in oklab, var(--color-base-content) 34%, transparent);
}

.demo-hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: color-mix(in oklab, var(--color-base-content) 52%, transparent);
}

.demo-form {
  display: grid;
  gap: 12px;
  max-width: 420px;
}

.demo-form label {
  display: grid;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 600;
  color: color-mix(in oklab, var(--color-base-content) 66%, transparent);
}

.demo-form input {
  width: 100%;
  padding: 8px 11px;
  border: 1px solid var(--color-base-300);
  border-radius: 8px;
  background: var(--color-base-100);
  color: var(--color-base-content);
  font-size: 14px;
  font-weight: 400;
}

.demo-form input:focus {
  outline: 2px solid color-mix(in oklab, var(--color-base-content) 34%, transparent);
  outline-offset: 1px;
}

.demo-form small {
  font-size: 11.5px;
  font-weight: 400;
  color: color-mix(in oklab, var(--color-base-content) 50%, transparent);
}

.demo-form .demo-button {
  justify-self: start;
  margin-top: 4px;
}

.demo-chip-label {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: color-mix(in oklab, var(--color-base-content) 46%, transparent);
}

.demo-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.demo-chips:last-child {
  margin-bottom: 0;
}

.demo-chip {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 14px;
  border: 1px solid var(--color-base-300);
  border-radius: 10px;
  background: var(--color-base-100);
  font-size: 13px;
  font-weight: 600;
  color: var(--color-base-content);
  text-decoration: none;
  transition: border-color 120ms ease;
}

.demo-chip:hover {
  border-color: color-mix(in oklab, var(--color-base-content) 32%, transparent);
}

.demo-chip span {
  font-size: 11.5px;
  font-weight: 400;
  color: color-mix(in oklab, var(--color-base-content) 50%, transparent);
}

.examples-outro {
  display: grid;
  gap: 16px;
  padding: 56px 0 0;
  border-top: 1px solid var(--color-base-300);
}

.examples-outro h2 {
  margin: 0;
  font-size: 21px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.examples-outro p {
  margin: 0;
  max-width: 68ch;
  font-size: 15px;
  line-height: 1.65;
  color: color-mix(in oklab, var(--color-base-content) 68%, transparent);
}

.examples-outro code,
.demo-hint code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  padding: 1px 5px;
  border-radius: 5px;
  background: color-mix(in oklab, var(--color-base-200) 70%, transparent);
}

/*
 * Not fine print: this is the one line telling a visitor where to find the
 * workspace id and page slug every block above needs, so it reads at the same
 * weight as .example-note rather than as a legal footer.
 */
.examples-footer {
  margin-top: 56px;
  padding-top: 24px;
  border-top: 1px solid var(--color-base-300);
  font-size: 13.5px;
  line-height: 1.6;
  color: color-mix(in oklab, var(--color-base-content) 58%, transparent);
}

.examples-footer p {
  margin: 0;
  max-width: 68ch;
}

.demo-modal {
  width: min(1000px, 92vw);
  max-width: none;
  max-height: 88vh;
  padding: 0;
  border: 1px solid var(--color-base-300);
  border-radius: 14px;
  background: var(--color-base-100);
  color: var(--color-base-content);
  overflow: hidden;
}

.demo-modal::backdrop {
  background: rgba(15, 17, 23, 0.55);
}

.demo-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-base-300);
  font-size: 14px;
}

.demo-modal-head button {
  border: 1px solid var(--color-base-300);
  border-radius: 7px;
  background: var(--color-base-100);
  color: var(--color-base-content);
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  cursor: pointer;
}

.demo-modal-slot {
  max-height: calc(88vh - 52px);
  overflow-y: auto;
  padding: 12px 16px 16px;
}

@media (max-width: 640px) {
  .examples-shell {
    padding: 48px 18px 72px;
  }

  .examples-title {
    font-size: 30px;
  }
}
</style>
